<template>
  <div class="l-stack l-gap-2 l-padded">
    <loading-overlay v-if="isLoading" />
    <div v-if="notifications.length > 0">
      <v-alert
        v-for="n in notifications"
        :type="n.type"
        :key="n.id"
        closable
        close-label="Close notification"
      >
        <template v-slot:text>
          <p v-for="p in n.content" :key="p" v-html="p"></p>
        </template>
      </v-alert>
    </div>
    <div v-if="getLatestNewsItem != undefined">
      <v-alert>
        <news-item :newsItem="getLatestNewsItem" />
        <router-link
          :to="{ name: 'News' }"
          target="_blank"
          class="external-link"
        >
          More news
        </router-link>
      </v-alert>
    </div>
    <section style="position: relative">
      <v-dialog
        transition="dialog-bottom-transition"
        activator="#indicator-info"
        contained
      >
        <template v-slot:default="{ isActive }">
          <v-card>
            <v-toolbar
              color="primary"
              :title="`Current ${countryStore.activeIndicator.name} Growth Forecast`"
            ></v-toolbar>
            <v-card-text>
              <div class="text-body-1 pa-12 pb-4">
                All displayed GDP growth projections on this website are
                non-annualized quarter-on-quarter growth rates in %. The GDP
                growth projections for the Euro Area are obtained by aggregating
                the GDP growth projections of all Euro Area member countries in
                the Nowcasting Lab. As an alternative to aggregating the Euro
                Area projections from the projections of individual countries,
                the lab also includes direct projection models for Euro Area GDP
                growth. Here, the variable selections, the model
                parameterizations, etc., are done independently of the
                projections for the individual countries. The direct Euro Area
                GDP growth projections are not displayed on the website, but run
                in the background and are available on request.
              </div>
              <div class="text-body-1 pa-12 pt-4">
                For Ireland, the non-annualized quarter-on-quarter growth rate
                of Modified Domestic Demand (MDD) in % is projected instead of
                the GDP growth rate. Unlike GDP, Irish MDD excludes exports and
                imports, as well as large transactions by foreign companies that
                do not have a major impact on the domestic economy. MDD is
                considered to be a more accurate measure of underlying Irish
                economic performance than GDP, as GDP is significantly distorted
                by the activities of multinational companies. The Central
                Statistics Office of Ireland provides
                <a
                  href="https://www.cso.ie/en/interactivezone/statisticsexplained/nationalaccountsexplained/totaldomesticdemandandmodifiedtotaldomesticdemand/"
                  target="_blank"
                  >more detailed information</a
                >.
              </div>
            </v-card-text>
            <v-card-actions class="justify-end">
              <v-btn variant="text" @click="isActive.value = false"
                >Close</v-btn
              >
            </v-card-actions>
          </v-card>
        </template>
      </v-dialog>
      <h1 class="title is-5 title-align-center">
        Current {{ countryStore.activeIndicator.name }} Growth Forecast
        {{ activeTargetPeriod }}: Country Overview
        <span id="indicator-info">
          <ncl-info-icon text="Click for more information" location="bottom" />
        </span>
      </h1>
      <h2 class="subtitle is-6 title-align-center">
        <detail-report-link :active-target-period="activeTargetPeriod">
          Inspect Country and Model Specific Details:
          {{ countryStore.activeCountry.name }}
          {{ activeTargetPeriod }}
          <v-icon icon="mdi-open-in-new"></v-icon>
        </detail-report-link>
      </h2>
      <div>
        <div class="columns is-vcentered is-desktop">
          <div class="column hide-on-mobile is-1">
            <MapLegend class="legend" :indicator-values="indicatorRange">
            </MapLegend>
          </div>
          <div class="column hide-on-mobile">
            <KOFCASTsMap
              class="kofmap"
              :country-colors="countryColors"
              :indicator-values="indicatorRange"
              :active-indicator="countryStore.activeIndicator.indicator"
              :active-target-period="activeTargetPeriod"
            >
            </KOFCASTsMap>
          </div>
          <div class="column histogram">
            <highcharts
              ref="highcharts"
              :options="histogramOptions"
            ></highcharts>
          </div>
        </div>
      </div>
    </section>
    <h1 class="title is-5 title-align-center">
      Forecast History for {{ countryStore.activeCountry.adjective }}
      {{ countryStore.activeIndicator.name }} Growth {{ activeTargetPeriod }}
    </h1>
    <div>
      <div class="column">
        <highcharts
          ref="highcharts"
          :options="seriesOptions"
          id="areaInfo"
        ></highcharts>
      </div>
    </div>
  </div>
</template>

<script>
import { usePlausible } from "v-plausible/vue";
import NewsItem from "@/components/NewsItem.vue";
import KOFCASTsMap from "@/components/NclMap.vue";
import MapLegend from "@/components/MapLegend.vue";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import DetailReportLink from "@/components/DetailReportLink.vue";
import NclInfoIcon from "@/components/NclInfoIcon.vue";
import {
  min as d3min,
  max as d3max,
  scaleLinear as d3scaleLinear,
  interpolateRgb as d3interpolateRgb,
} from "d3";
import { mapState, mapStores } from "pinia";
import { useCountryStore } from "@/stores/country";
import { useNewsStore } from "@/stores/news";
import { useNotificationsStore } from "@/stores/notifications";
import { useHighchartsTheme } from "@/composables/useHighchartsTheme";

export default {
  setup() {
    const { trackEvent, trackPageview } = usePlausible();
    const { isDark, getHighchartsTheme } = useHighchartsTheme();
    return { trackEvent, trackPageview, isDark, getHighchartsTheme };
  },
  components: {
    NewsItem,
    KOFCASTsMap,
    MapLegend,
    LoadingOverlay,
    DetailReportLink,
    NclInfoIcon,
  },
  computed: {
    ...mapStores(useCountryStore),
    ...mapState(useCountryStore, ["activeCountry", "activeIndicator"]),
    ...mapState(useNewsStore, ["getLatestNewsItem"]),
    ...mapState(useNotificationsStore, ["notifications"]),
    currentIndicatorValue() {
      const val = this.countryStore.getIndicatorValueFromCountry(
        this.countryStore.activeCountry.code,
        this.countryStore.activeIndicator.indicator,
        this.activeTargetPeriod,
      );
      if (val) {
        const indicatorValue = val.toFixed(2);
        return indicatorValue > 0
          ? `+${indicatorValue}`
          : indicatorValue.toString();
      }
      return "";
    },
    minMaxTargetPeriodDates() {
      if (
        this.countryStore.activeCountry &&
        this.countryStore.activeIndicator.indicator &&
        this.activeTargetPeriod
      ) {
        let data = this.countryStore.minMaxTargetPeriodDates(
          this.countryStore.activeCountry.code,
          this.countryStore.activeIndicator.indicator,
          this.activeTargetPeriod,
        );
        return data;
      }
      return [null, null];
    },
    currentDate() {
      const options = { year: "numeric", month: "short", day: "numeric" };
      const data = this.countryStore.getIndicatorTPHistory(
        this.countryStore.activeCountry,
        this.countryStore.activeIndicator.indicator,
        this.activeTargetPeriod,
      );
      if (data.length > 0) {
        const date = new Date(data[data.length - 1][0]);
        return date.toLocaleDateString("en-US", options);
      }
      return "";
    },
    indicatorRange() {
      let min = d3min(this.countryStore.areas, (d) => {
        if (this.countryStore.activeIndicator) {
          return this.countryStore.getIndicatorValueFromCountry(
            d.code,
            this.countryStore.activeIndicator.indicator,
            this.activeTargetPeriod,
          );
        }
        return 0;
      });

      let max = d3max(this.countryStore.areas, (d) => {
        if (this.countryStore.activeIndicator) {
          return this.countryStore.getIndicatorValueFromCountry(
            d.code,
            this.countryStore.activeIndicator.indicator,
            this.activeTargetPeriod,
          );
        }
        return 0;
      });

      return [min, max];
    },
    countryColors() {
      let minMax = this.indicatorRange;
      let firstColor = this.countryStore.colors[0];
      let lastColor =
        this.countryStore.colors[this.countryStore.colors.length - 1];

      let colorRange = [firstColor, lastColor];
      const color = d3scaleLinear()
        .domain(minMax)
        .range(colorRange)
        .interpolate(d3interpolateRgb, this.countryStore.colors);

      let colors = {};
      for (let a in this.countryStore.areas) {
        let area = this.countryStore.areas[a];
        for (let x in area.countries) {
          let id = area.countries[x];
          let indicator = this.countryStore.getIndicatorValueFromCountry(
            area.code,
            this.countryStore.activeIndicator.indicator,
            this.activeTargetPeriod,
          );
          if (indicator == null) {
            colors[id] = {
              color: null,
            };
          } else {
            colors[id] = {
              color: color(indicator),
            };
          }
        }
      }
      return colors;
    },
    currentTrend() {
      /* Calculates 7-Day forecasting trend:
       *  If there are less than eight forecasts subtract the newest entry in the array from the oldest
       *  Else: Calculate seven-day window and subtract newest entry of array from the oldest in the window
       */
      const data = this.countryStore.getIndicatorTPHistory(
        this.countryStore.activeCountry,
        this.countryStore.activeIndicator.indicator,
        this.activeTargetPeriod,
      );
      const forecastWindow = 8;
      let latestForecast = 0;
      let oldestForecast = 0;
      if (data.length > 0) {
        latestForecast = data[data.length - 1][1];
        oldestForecast =
          data.length < forecastWindow
            ? data[0][1]
            : data[data.length - forecastWindow][1];
      }
      return latestForecast - oldestForecast;
    },
    activeTargetPeriod: {
      get() {
        if (this.countryStore.selectedTargetPeriod) {
          return this.countryStore.selectedTargetPeriod;
        }
        if (this.countryStore.quarters.length) {
          return this.countryStore.quarters[1].name;
        }
        return "";
      },
      set(newPeriod) {
        this.countryStore.selectedTargetPeriod = newPeriod;
      },
    },
    seriesOptions() {
      const data = this.countryStore.getIndicatorTPHistory(
        this.countryStore.activeCountry,
        this.countryStore.activeIndicator.indicator,
        this.activeTargetPeriod,
      );

      const series = {
        name: "GDP growth forecast",
        showInLegend: false,
        marker: {
          enabled: false,
        },
        data: data,
      };

      const theme = this.getHighchartsTheme(this.isDark);

      return {
        ...theme,
        chart: {
          ...theme.chart,
          style: {
            ...theme.chart.style,
            fontSize: "14px",
          },
        },
        title: {
          ...theme.title,
          text: "",
          style: {
            ...theme.title.style,
            fontWeight: "bold",
          },
        },
        yAxis: [
          {
            ...theme.yAxis,
            title: {
              ...theme.yAxis.title,
              text: "Quarter-on-quarter growth in %",
            },
            minRange: 0.2,
          },
        ],
        xAxis: {
          ...theme.xAxis,
          title: {
            ...theme.xAxis.title,
            text: "Date of forecast",
          },
          type: "datetime",
          dateTimeLabelFormats: {
            month: "%b %Y",
            year: "%b %Y",
          },
          tickInterval: 30 * 24 * 3600 * 1000,
          min: this.minMaxTargetPeriodDates[0],
          max: this.minMaxTargetPeriodDates[1],
        },
        tooltip: {
          ...theme.tooltip,
          valueDecimals: 2,
          valueSuffix: "%",
        },
        series: [series],
      };
    },
    histogramOptions() {
      const series = {
        name: "GDP growth forecast",
        showInLegend: false,
        data: this.countryStore.areas
          .map((area) => {
            const color =
              this.countryStore.activeCountry.name === area.name
                ? "#FFA500"
                : "#7cb5ec";

            const value = this.countryStore.getIndicatorValueFromCountry(
              area.code,
              this.countryStore.activeIndicator.indicator,
              this.activeTargetPeriod,
            );

            return {
              y: value,
              name: area.name,
              color: color,
            };
          })
          .sort((a, b) => {
            return b.y - a.y;
          }),
      };

      const theme = this.getHighchartsTheme(this.isDark);

      return {
        ...theme,
        chart: {
          ...theme.chart,
          type: "bar",
        },
        title: {
          ...theme.title,
          text: "",
          style: {
            ...theme.title.style,
            fontWeight: "bold",
          },
        },
        xAxis: {
          ...theme.xAxis,
          type: "category",
          labels: {
            ...theme.xAxis.labels,
            style: {
              ...theme.xAxis.labels.style,
              fontSize: "14px",
            },
          },
        },
        yAxis: [
          {
            ...theme.yAxis,
            title: {
              ...theme.yAxis.title,
              text: "Quarter-on-quarter growth in %",
              style: {
                ...theme.yAxis.title.style,
                fontSize: "13px",
              },
            },
            labels: {
              ...theme.yAxis.labels,
              style: {
                ...theme.yAxis.labels.style,
                fontSize: "13px",
              },
            },
          },
        ],
        tooltip: {
          ...theme.tooltip,
          valueDecimals: 2,
          valueSuffix: " %",
        },
        series: [series],
        plotOptions: {
          series: {
            cursor: "pointer",
            point: {
              events: {
                click: (d) => {
                  this.countryStore.setActiveCountry(d.point.name);
                },
              },
            },
          },
        },
      };
    },
  },
  methods: {
    async loadGraphs(targetPeriod) {
      this.isLoading = true;
      await this.countryStore.loadData(targetPeriod);
      this.isLoading = false;
    },
    updateFavicon(countryCode) {
      const canvas = document.createElement("canvas");
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext("2d");
      const flagEmoji =
        countryCode === "ea_agg"
          ? "\uD83C\uDDEA\uD83C\uDDFA"
          : Array.from(countryCode.toUpperCase())
              .map((c) => String.fromCodePoint(c.codePointAt(0) + 127397))
              .join("");
      ctx.font = "28px serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(flagEmoji, 16, 18);
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = canvas.toDataURL();
    },
  },
  watch: {
    "countryStore.selectedTargetPeriod"(newPeriod) {
      if (newPeriod) {
        this.loadGraphs(newPeriod);
      }
    },
    "countryStore.activeCountry"(newCountry) {
      if (newCountry?.code) {
        this.updateFavicon(newCountry.code);
      }
    },
  },
  data() {
    return {
      valueNames: [{ name: "GDP", indicator: "gdp" }],
      openNavigation: false,
      isLoading: true,
      isIndicatorModalOpen: false,
    };
  },
  async mounted() {
    this.isLoading = true;

    // Ensure quarters are loaded (may already be loaded by App.vue)
    if (this.countryStore.quarters.length === 0) {
      await this.countryStore.loadQuarters();
    }

    await this.loadGraphs(this.activeTargetPeriod);
    this.updateFavicon(this.countryStore.activeCountry.code);
    this.trackPageview(undefined, {});
  },
};
</script>

<style lang="scss">
@import url("bulma/css/bulma.min.css");

.navbar-item .navbar-dropdown.is-boxed {
  visibility: visible;
}

@media (min-width: 1088px) {
  .navbar-item.is-hoverable:hover .navbar-dropdown {
    display: block;
  }

  .navbar-item.is-hoverable:hover .navbar-dropdown.is-boxed {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
}

html,
body {
  margin: 0;
  padding: 0;
  min-height: 100vh;
  // clip, not hidden: hidden makes body a scroll container, which kills
  // position: sticky on the navbar
  overflow-x: clip;
}

.kofmap {
  width: 100%;
  height: 40vh;
}

// min-width: auto on flex columns stops Highcharts from reflowing smaller
// after a window shrink or browser zoom — chart and column hold each other
// wide. min-width: 0 lets them shrink.
.columns > .column {
  min-width: 0;
}

.histogram {
  margin-top: auto;
}

.hide-on-mobile {
  display: flex;
}

@media screen and (max-width: 500px) {
  .hide-on-mobile {
    display: none;
  }
}

.navbar-item.dropdown-option {
  cursor: pointer;
}

.navbar-item.dropdown-option:hover {
  background-color: #eee;
}

.title-align-center {
  text-align: center;
}

.link:hover {
  color: rgb(124, 181, 236);
}

.navbar-link:hover {
  color: rgb(124, 181, 236);
}

.navbar-link:not(.is-arrowless)::after {
  border-color: rgb(124, 181, 236);
}

.button.is-rounded:hover {
  border-color: rgb(124, 181, 236);
}

@media screen and (min-width: 1023px) and (max-width: 1380px) {
  .navbar-item.show-on-wide-small {
    display: None;
  }

  .navbar-item.hide-on-wide-small {
    display: flex;
  }
}

// Overlays the map's empty ocean area; its column collapses to zero height
.legend {
  position: absolute;
}

h1 .info-icon {
  font-size: 12pt;
}
</style>
